import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Checkbox} from '@mui/material';
import {
    DndContext,
    DragOverlay,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import {SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy} from '@dnd-kit/sortable';
import {goalActions, httpRequestsOnSuccessActions, sessionActions} from 'store';
import {
    FindLoad,
    FindSuccessItem,
    Images,
    renderGoalTypes,
    useBaseModal,
    User,
    useSecondaryModal,
} from 'utils';
import {
    BadgeLine,
    CreateChancel,
    Loader,
    NoItemsYet,
    SelectTypeAutocomplete,
    SingleSearch,
    SkillCard,
    TagBox
} from 'components';
import {GoalDetails} from 'fragments';
import {
    goalOrderOptions,
    goalSortTypeEnums,
    sortOrderEnums
} from 'fragments/client/clientSingle/clientClinical/goals/constants';
import {SortableGoalRow} from './core/sortableGoalRow';

const ACTION_TYPE = 'GET_GOALS_FOR_SESSION';
const CREATE_ACTION_TYPE = 'CREATE_SESSION';
const EDIT_GOALS_ACTION_TYPE = 'EDIT_SESSION_GOALS';

const compareGoalNames = (a, b) => (a?.name || '').localeCompare(b?.name || '', 'en', {
    sensitivity: 'base',
    numeric: true
});
const goalTime = (goal) => {
    const time = new Date(goal?.createdAt).getTime();
    return Number.isNaN(time) ? 0 : time;
};

/** A copy of `goals` in the order of one `goalOrderOptions` entry (ties keep their current order). */
const sortGoals = (goals, option) => {
    const direction = option?.orderType === sortOrderEnums.DESC ? -1 : 1;
    const compare =
        option?.sortType === goalSortTypeEnums.CREATED_AT ? (a, b) => goalTime(a) - goalTime(b) : compareGoalNames;
    return [...goals].sort((a, b) => direction * compare(a, b));
};

/**
 * Session Preparation: pick, pin and order the goals of a session.
 *
 * Live sessions (default): `info` absent → creates the session for the appointment in the store;
 * `info` set → edits that session's goals. Manual data entry passes `onConfirm` instead: the
 * picked `[{ goal, pinned }]` (in display order) is handed back and nothing is dispatched.
 * `lockedGoalIds` pre-selects goals that must stay selected (they already hold data). `initialGoals`
 * (`[{ goal, pinned }]`, manual only) is the selection to start from — the manual screen's Edit Goals.
 * `goalsQuery` (manual only, `manualGoalsQuery`) keeps the edited session's targets that hold data listed.
 */
export const CreateSession = ({
                                  afterSuccess,
                                  info,
                                  clientId,
                                  onConfirm,
                                  onCancel,
                                  confirmText,
                                  lockedGoalIds,
                                  initialGoals,
                                  title,
                                  goalsQuery,
                              }) => {
    const appt = useSelector((state) => state.appointment.appointmentById);
    const goalsForSession = useSelector((state) => state.goals.goalsForSession);
    const dispatch = useDispatch();
    const loader = FindLoad(ACTION_TYPE);
    const loadCreation = FindLoad(info ? EDIT_GOALS_ACTION_TYPE : CREATE_ACTION_TYPE);
    const success = FindSuccessItem(info ? EDIT_GOALS_ACTION_TYPE : CREATE_ACTION_TYPE);
    const [selectedList, setSelectedList] = useState([]);
    const [orderedGoals, setOrderedGoals] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortId, setSortId] = useState(null);
    const {openBase} = useBaseModal();
    const {closeSecondary} = useSecondaryModal();
    const {userInfo} = User();
    const forceAllGoals = !onConfirm && !!userInfo?.clinicalSettings?.includeAllActiveGoalsInSessions;
    const sensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 8}}),
        useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
    );

    const displayGoals = orderedGoals?.length ? orderedGoals : goalsForSession || [];
    const visibleGoals = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        return term ? displayGoals.filter((g) => (g?.name || '').toLowerCase().includes(term)) : displayGoals;
    }, [displayGoals, searchTerm]);

    const goalsClientId = clientId || appt?.client?.id;

    const goalsQueryKey = JSON.stringify(goalsQuery ?? null);
    useEffect(() => {
        dispatch(goalActions.getGoalsForSession(goalsClientId, undefined, goalsQuery));
    }, [dispatch, goalsClientId, goalsQueryKey]);


    useEffect(() => {
        setSortId(null);
        if (!goalsForSession?.length) {
            setOrderedGoals([]);
            return;
        }
        const savedOrder = info?.goals?.map((g) => g?.goal?.id) || initialGoals?.map((g) => g?.goal) || [];
        if (savedOrder.length) {
            const byId = new Map(goalsForSession.map((g) => [g?.id, g]));
            const ordered = [];
            savedOrder.forEach((id) => {
                if (byId.has(id)) {
                    ordered.push(byId.get(id));
                    byId.delete(id);
                }
            });
            goalsForSession.forEach((g) => {
                if (byId.has(g?.id)) ordered.push(g);
            });
            setOrderedGoals(ordered);
        } else {
            setOrderedGoals(goalsForSession);
        }
    }, [goalsForSession, info?.id]);

    useEffect(() => {
        if (info?.goals?.length) {
            const preSelected = info.goals.map((g) => ({
                goal: g?.goal?.id,
                pinned: g?.pinned || false,
            }));
            setSelectedList(preSelected);
        } else if (initialGoals?.length) {
            setSelectedList(initialGoals.map((g) => ({goal: g?.goal, pinned: !!g?.pinned})));
        }
    }, [info]);

    // Manual entry starts like a live session, with nothing selected, except goals that already hold
    // data on the session being edited: those are pre-selected and locked so their data can't be dropped.
    // Merged onto the current selection so a late-arriving lock list doesn't wipe the user's picks.
    useEffect(() => {
        if (!onConfirm || !lockedGoalIds?.length) return;
        setSelectedList((prev) => {
            const byGoal = new Map(prev.map((s) => [s?.goal, s]));
            lockedGoalIds.forEach((id) => {
                if (id && !byGoal.has(id)) byGoal.set(id, {goal: id, pinned: false});
            });
            return Array.from(byGoal.values());
        });
    }, [onConfirm ? (lockedGoalIds || []).join() : null]);

    // When the org forces "all active goals in every session", auto-include every loaded goal.
    // Merges onto any existing selection so pinned/edit-mode state is preserved and nothing is dropped.
    useEffect(() => {
        if (!forceAllGoals || !goalsForSession?.length) return;
        setSelectedList((prev) => {
            const byGoal = new Map(prev.map((s) => [s?.goal, s]));
            goalsForSession.forEach((g) => {
                if (g?.id && !byGoal.has(g.id)) byGoal.set(g.id, {goal: g.id, pinned: false});
            });
            return Array.from(byGoal.values());
        });
    }, [forceAllGoals, goalsForSession, info]);

    useEffect(() => {
        if (success && !onConfirm) {
            afterSuccess();
            dispatch(httpRequestsOnSuccessActions.removeSuccess(info ? EDIT_GOALS_ACTION_TYPE : CREATE_ACTION_TYPE));
            closeSecondary();
        }
    }, [success]);

    const originalGoalIds = [...(info?.goals?.map((g) => g?.goal?.id) || []), ...(lockedGoalIds || [])];

    const handleCheck = (ev, item) => {
        ev.stopPropagation();
        ev.preventDefault();
        const id = item?.id;

        // When the org forces all goals, selection is locked — no goal can be added or removed.
        if (forceAllGoals) return;

        // In edit mode (or for goals already holding manual data) the goal cannot be unchecked
        if (originalGoalIds.includes(id)) return;

        const params = [...selectedList];

        const current = params?.find((i) => i?.goal === id);

        if (current) {
            const index = params.indexOf(current);
            if (index > -1) {
                params.splice(index, 1);
            }
        } else {
            params.push({
                goal: id,
                pinned: false,
            });
        }
        setSelectedList(params);
    };

    const handlePin = (goalItem) => {
        const goalId = goalItem?.id || goalItem?.goal;
        if (!goalId) return;

        const updatedList = selectedList.map((item) => {
            if (item.goal === goalId) {
                return {...item, pinned: !item.pinned};
            }
            return item;
        });
        setSelectedList(updatedList);
    };

    const handleDragStart = ({active}) => setActiveId(String(active?.id));

    const handleSort = (id) => {
        const option = goalOrderOptions.find((o) => o.id === id);
        if (!option) return;
        setSortId(id);
        setOrderedGoals((prev) => sortGoals(prev?.length ? prev : goalsForSession || [], option));
    };

    const handleDragEnd = ({active, over}) => {
        setActiveId(null);
        if (!over || active.id === over.id) return;
        setSortId(null);
        setOrderedGoals((prev) => {
            const base = prev?.length ? prev : goalsForSession || [];
            const from = base.findIndex((g) => String(g?.id) === String(active.id));
            const to = base.findIndex((g) => String(g?.id) === String(over.id));
            if (from === -1 || to === -1) return base;
            return arrayMove(base, from, to);
        });
    };

    // Build the payload in the current display order (drag order), keeping each
    // selection's pinned state. Any selected goal missing from the display list
    // (e.g. an archived goal still on the session in edit mode) is appended so it
    // is never silently dropped.
    const orderedSelection = () => {
        const seen = new Set();
        const ordered = [];
        displayGoals.forEach((g) => {
            const selection = selectedList.find((k) => k?.goal === g?.id);
            if (selection) {
                ordered.push(selection);
                seen.add(selection.goal);
            }
        });
        selectedList.forEach((selection) => {
            if (!seen.has(selection?.goal)) ordered.push(selection);
        });
        return ordered;
    };

    const handleCreate = () => {
        const goals = orderedSelection();
        if (onConfirm) {
            onConfirm(goals);
            return;
        }
        if (info) {
            dispatch(sessionActions.editSessionGoals(info?.id, {goals}));
        } else {
            const params = {
                appt: appt?.id,
                goals,
            };
            dispatch(sessionActions.createSession(params, appt?.id));
        }
    };

    // Row body (checkbox + goal card) — shared by the sortable list row and the
    // DragOverlay clone so the floating preview looks identical to the source row.
    const renderGoalInner = (i) => {
        const currentGoal = selectedList?.find((k) => k?.goal === i?.id);
        return (
            <>
                <Checkbox
                    onClick={(e) => handleCheck(e, i)}
                    name={'checkBox'}
                    disabled={forceAllGoals || originalGoalIds.includes(i?.id)}
                    style={{
                        padding: 0,
                        color: '#347AF0',
                        opacity: forceAllGoals || originalGoalIds.includes(i?.id) ? 0.5 : 1,
                    }}
                    checked={currentGoal?.goal === i?.id}
                    // checked={selectedList?.find((k) => k?.goal === i?.id)}
                />

                <div className="session-gol-box">
                    <div className="flex-align-center" style={{gap: 12}}>
                        <button
                            className="goal-link-btn"
                            onClick={() => {
                                openBase(
                                    <GoalDetails
                                        goalId={i?.id}
                                        requestParams={{clientId: goalsClientId}}
                                        clientId={goalsClientId}
                                        viewOnly
                                    />
                                );
                            }}
                        >
                            {i?.name}
                        </button>
                        <SkillCard type={i?.type}/>
                        {i?.tags && !!i?.tags?.length && (
                            <div className='flex-align-center' style={{gap: 12}}>
                                <BadgeLine/>
                                <TagBox tags={i?.tags} count={3} wrapperStyles={{marginBottom: 0}}/>
                            </div>
                        )}
                    </div>

                    <div className="flex-align-center" style={{gap: 12}}>
                        <div className="correct-goal-box">{renderGoalTypes(i?.dataType)}</div>
                        <button
                            disabled={!currentGoal}
                            style={{opacity: currentGoal ? 1 : 0.5}}
                            onClick={() => handlePin(i)}
                            className={currentGoal?.pinned ? 'pin-goal-box-active' : 'pin-goal-box'}
                        >
                            <img src={Images.pushPin} alt="pin"/>
                        </button>
                    </div>
                </div>
            </>
        );
    };

    const activeGoal = displayGoals.find((g) => String(g?.id) === String(activeId));

    // Select All / Clear All act only on the goals the search leaves visible, so a
    // filtered list never selects (or clears) goals the user cannot see.
    const selectableGoals = visibleGoals.filter((i) => !originalGoalIds.includes(i?.id));
    const allSelectableSelected =
        selectableGoals.length > 0 && selectableGoals.every((i) => selectedList?.some((k) => k?.goal === i?.id));

    if (loader?.length) {
        return (
            <div style={{width: 1200, maxWidth: '100%', height: '75vh'}}>
                <Loader/>
            </div>
        );
    }

    return (

        <div style={{width: 1200, maxWidth: '100%', height: '80vh'}}>
            <p className="modal-header-title">{info ? 'Edit Session Goals' : 'Session Preparation'}</p>
            <div className="session-goals-toolbar">
                <SingleSearch
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    placeholder="Search goal"
                    className="search-setting-input session-goals-search"
                />
                <div className="session-goals-sort">
                    <SelectTypeAutocomplete
                        disableClearable={true}
                        name={'sessionGoalSort'}
                        placeholder="Sort by"
                        handleSelect={(event) => handleSort(event.target.value)}
                        defaultValue={sortId}
                        list={goalOrderOptions}
                        renderValue={(i) => i?.name}
                        uiType={'tableFilter'}
                        noError={true}
                    />
                </div>
                <button
                    disabled={forceAllGoals}
                    style={{opacity: forceAllGoals ? 0.5 : 1}}
                    onClick={() => {
                        if (allSelectableSelected) {
                            // Clear All: remove the shown selectable goals; locked and hidden selections stay
                            const shownIds = new Set(selectableGoals.map((i) => i?.id));
                            setSelectedList(selectedList.filter((k) => !shownIds.has(k?.goal)));
                        } else {
                            // Select All: add the shown selectable goals
                            const newGoals = selectableGoals
                                .filter((i) => !selectedList?.some((k) => k?.goal === i?.id))
                                .map((i) => ({goal: i?.id, pinned: false}));
                            setSelectedList([...selectedList, ...newGoals]);
                        }
                    }}
                    className="session-select-all-btn"
                >
                    {allSelectableSelected ? 'Clear All' : 'Select All'}
                </button>
            </div>

            <div className="session-preparation-body">
                {displayGoals?.length && !visibleGoals.length ? (
                    <p className="session-goals-no-match">No goals match “{searchTerm.trim()}”.</p>
                ) : displayGoals?.length ? (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onDragCancel={() => setActiveId(null)}
                    >
                        <SortableContext items={visibleGoals.map((g) => String(g?.id))}
                                         strategy={verticalListSortingStrategy}>
                            {visibleGoals.map((i) => (
                                <SortableGoalRow key={i?.id} id={String(i?.id)}>
                                    {renderGoalInner(i)}
                                </SortableGoalRow>
                            ))}
                        </SortableContext>
                        <DragOverlay>
                            {activeGoal ? (
                                <div className="session-goal-row session-goal-row--overlay">
                                    <button type="button" className="goal-drag-handle" aria-hidden="true">
                                        <img src={Images.dragIcon} alt="drag"/>
                                    </button>
                                    {renderGoalInner(activeGoal)}
                                </div>
                            ) : null}
                        </DragOverlay>
                    </DndContext>
                ) : (
                    <NoItemsYet text={'No Goals Information'} image={Images.noClientYet}/>
                )}
            </div>

            <div style={{marginBottom: 24}}>
                <CreateChancel
                    onCreate={handleCreate}
                    onClose={onCancel || closeSecondary}
                    disabled={!selectedList?.length}
                    loader={!onConfirm && !!loadCreation.length}
                    create={confirmText || (info ? 'Update Goals' : 'Start Session')}
                    chancel={'Cancel'}
                    buttonWidth="100%"
                    cancelRight={'24px'}
                />
            </div>
        </div>
    );
};
